import { ShieldAlert } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { ListCard } from "@/components/ui/list-card";

import { nodeTitle } from "../../caseFiles/nodeNames";
import {
  DOCUMENT_ROLE_LABELS,
  type DocumentRole,
  type FsNode
} from "../../caseFiles/types";

type Props = {
  nodes: FsNode[];
  onSetRole: (nodeIds: string[], role: DocumentRole) => void;
};

/**
 * Files whose role waits on an attorney: `privileged` look internal (read so
 * automatically, still in the packet until confirmed, or tagged before reads
 * only suggested), `suggested` look like evidence.
 */
export const roleReviewItems = (nodes: FsNode[]) => {
  const privileged: FsNode[] = [];
  const suggested: FsNode[] = [];
  for (const node of nodes.filter((n) => n.kind === "file")) {
    if (node.document_role_suggestion === "internal") privileged.push(node);
    else if (node.document_role_suggestion) suggested.push(node);
    else if (
      node.document_role === "internal" &&
      node.document_role_source &&
      node.document_role_source !== "attorney"
    )
      privileged.push(node);
  }
  return { privileged, suggested };
};

const count = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

export const roleReviewSummary = (privileged: number, suggested: number) =>
  [
    privileged
      ? `${count(privileged, "file looks", "files look")} internal`
      : null,
    suggested
      ? `${count(suggested, "file looks", "files look")} like evidence`
      : null
  ]
    .filter(Boolean)
    .join(", ");

type RowProps = {
  node: FsNode;
  reason?: string | null;
  primary: string;
  onPrimary: () => void;
  secondary: string;
  onSecondary: () => void;
};

const ReviewRow: FC<RowProps> = ({
  node,
  reason,
  primary,
  onPrimary,
  secondary,
  onSecondary
}) => (
  <div className="flex flex-col gap-1 px-3 py-2" data-node-review={node.id}>
    <span className="truncate text-sm text-fg" title={node.path_cache}>
      {nodeTitle(node)}
    </span>
    {reason ? <span className="text-sm text-fg-3">{reason}</span> : null}
    <div className="flex gap-1">
      <Button size="xs" onClick={onPrimary}>
        {primary}
      </Button>
      <Button variant="ghost" size="xs" onClick={onSecondary}>
        {secondary}
      </Button>
    </div>
  </div>
);

/**
 * Automatic privilege readings as suggestions: the attorney confirms each one
 * (which leaves it out of the packet) or changes it. Either choice moves the
 * role to the attorney, which clears it here.
 */
export const RoleReviewCard: FC<Props> = ({ nodes, onSetRole }) => {
  const { privileged, suggested } = useMemo(
    () => roleReviewItems(nodes),
    [nodes]
  );
  const [open, setOpen] = useState(false);
  if (!privileged.length && !suggested.length) return null;

  return (
    <ListCard tone="warn" className="mx-2 my-1" data-testid="role-review">
      <div className="flex items-center gap-2 px-3 py-2">
        <ShieldAlert className="size-3.5 shrink-0 text-warn" />
        <span className="min-w-0 flex-1 truncate text-sm text-fg">
          {roleReviewSummary(privileged.length, suggested.length)}
        </span>
        <Button variant="ghost" size="xs" onClick={() => setOpen((o) => !o)}>
          {open ? "Hide" : "Review"}
        </Button>
      </div>
      {open ? (
        <>
          {suggested.map((node) => {
            const role = node.document_role_suggestion as DocumentRole;
            return (
              <ReviewRow
                key={node.id}
                node={node}
                reason={node.document_role_suggestion_reason}
                primary={`Mark ${DOCUMENT_ROLE_LABELS[role]}`}
                onPrimary={() => onSetRole([node.id], role)}
                secondary="Keep Internal"
                onSecondary={() => onSetRole([node.id], "internal")}
              />
            );
          })}
          {privileged.map((node) => (
            <ReviewRow
              key={node.id}
              node={node}
              reason={
                node.document_role_suggestion === "internal"
                  ? node.document_role_suggestion_reason
                  : node.document_role_reason
              }
              primary="Confirm"
              onPrimary={() => onSetRole([node.id], "internal")}
              secondary="Evidence"
              onSecondary={() => onSetRole([node.id], "evidence")}
            />
          ))}
          {privileged.length > 1 ? (
            <div className="flex justify-end px-3 py-2">
              <Button
                size="xs"
                onClick={() =>
                  onSetRole(
                    privileged.map((n) => n.id),
                    "internal"
                  )
                }
              >
                Confirm All Internal
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </ListCard>
  );
};
